/**
 * Lock-screen previews.
 *
 * Shows the notification as Android and iOS actually render it - including the
 * truncation. An operator who can see that their 90-character title becomes
 * "Your laundry pile is getting am…" writes a better one, and no amount of
 * character-count warnings substitutes for seeing it.
 *
 * The two platforms are drawn DIFFERENTLY on purpose. Android (Material 3) puts
 * the app name and time on one line above a bold title; iOS stacks a translucent
 * card with the app name upper-left and the timestamp upper-right, and gives the
 * title less weight relative to the body. Rendering one generic card twice would
 * look tidier and tell the operator nothing - the point of two previews is that
 * the same copy behaves differently on each.
 *
 * The image is a thumbnail, not a hero, because that is what a collapsed
 * notification shows on both platforms; the copy has to work at that size.
 */
import { useColors } from '../../../hooks/useColors'
import { PLACEHOLDER_BODY, PLACEHOLDER_TITLE } from '../constants'
import rapidryMark from '../../../assets/rapidry-mark.png'

type Props = {
  title: string
  body: string
  imageUrl?: string | null
  ctaLabel?: string | null
  platform: 'android' | 'ios'
  /** Compact drops the wallpaper frame - used inside dense list rows. */
  compact?: boolean
}

/** Rough per-platform single-line budgets on a modern phone at default text size. */
const LIMITS = {
  android: { title: 40, body: 78 },
  ios: { title: 34, body: 92 },
}

const truncate = (value: string, limit: number) =>
  value.length > limit ? `${value.slice(0, limit - 1).trimEnd()}…` : value

/**
 * The Rapidry app icon, at notification size.
 *
 * The real mark, not a monogram or an emoji: this is the one place in the
 * Notification Center where the brand belongs, because it is the one place the
 * customer actually sees it. The asset is the collar mark cropped tight and
 * downscaled to 128px, so it stays legible at the 16-19px an OS actually renders
 * a notification icon at.
 */
function AppMark({ size = 22 }: { size?: number }) {
  return (
    <img
      src={rapidryMark}
      alt=""
      width={size}
      height={size}
      style={{
        width: size,
        height: size,
        // iOS uses a superellipse and Android a circle; a soft square reads
        // correctly as an app icon on both without pretending to be either.
        borderRadius: size / 3.4,
        flexShrink: 0,
        display: 'block',
        objectFit: 'cover',
      }}
      aria-hidden
    />
  )
}

export default function NotificationPreview({
  title,
  body,
  imageUrl,
  ctaLabel,
  platform,
  compact = false,
}: Props) {
  const colors = useColors()
  const limits = LIMITS[platform]
  const isIos = platform === 'ios'

  const shownTitle = truncate(title || PLACEHOLDER_TITLE, limits.title)
  const shownBody = truncate(body || PLACEHOLDER_BODY, limits.body)

  const card = isIos ? (
    // iOS: translucent, heavily rounded, app name upper-left, time upper-right.
    <div
      style={{
        background: 'rgba(255,255,255,0.14)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: '17px',
        padding: '11px 13px',
        color: '#FFFFFF',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
        <AppMark size={19} />
        <span
          style={{
            fontSize: '10.5px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.72)',
          }}
        >
          Rapidry
        </span>
        <span style={{ marginLeft: 'auto', fontSize: '10.5px', color: 'rgba(255,255,255,0.55)' }}>
          now
        </span>
      </div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.3 }}>{shownTitle}</div>
          <div style={{ fontSize: '13px', lineHeight: 1.35, color: 'rgba(255,255,255,0.88)', marginTop: '1px' }}>
            {shownBody}
          </div>
        </div>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            style={{ width: '36px', height: '36px', borderRadius: '7px', objectFit: 'cover', flexShrink: 0 }}
          />
        ) : null}
      </div>
      {ctaLabel ? (
        <div style={{ marginTop: '8px', fontSize: '12px', fontWeight: 600, color: '#8FD4C1' }}>
          {ctaLabel}
        </div>
      ) : null}
    </div>
  ) : (
    // Android: opaque surface, icon + app name + time on one line, bold title.
    <div
      style={{
        background: '#FBFAF7',
        borderRadius: '13px',
        padding: '11px 13px',
        color: '#101418',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '5px' }}>
        <AppMark size={16} />
        <span style={{ fontSize: '11px', fontWeight: 600, color: '#5B6660' }}>Rapidry</span>
        <span style={{ fontSize: '11px', color: '#8A948E' }}>· now</span>
      </div>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13.5px', fontWeight: 700, lineHeight: 1.25 }}>{shownTitle}</div>
          <div style={{ fontSize: '12.5px', lineHeight: 1.4, color: '#3C4640', marginTop: '2px' }}>
            {shownBody}
          </div>
        </div>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            style={{ width: '38px', height: '38px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
          />
        ) : null}
      </div>
      {ctaLabel ? (
        <div
          style={{
            marginTop: '9px',
            paddingTop: '8px',
            borderTop: '1px solid #E8E4DA',
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            color: '#0F6E5A',
          }}
        >
          {ctaLabel}
        </div>
      ) : null}
    </div>
  )

  if (compact) return card

  return (
    <div
      style={{
        // A dark forest "wallpaper" so the card reads the way it does on a lock
        // screen, in both dashboard themes. Brand-tinted rather than the generic
        // blue-grey it used to be.
        background: 'linear-gradient(168deg, #17322D 0%, #0B1F1C 100%)',
        borderRadius: '12px',
        padding: '12px',
        border: `1px solid ${colors.border}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '6px',
          padding: '0 2px 9px',
        }}
      >
        <span
          style={{
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.82)',
          }}
        >
          {isIos ? 'iOS' : 'Android'}
        </span>
        <span
          style={{
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.38)',
          }}
        >
          Lock screen
        </span>
      </div>
      {card}
    </div>
  )
}
